import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

const ProbabilityHeaderActions = React.memo(({ onMaximize, fullScreen, onToggle }) => (
    <>
        {onMaximize && !fullScreen && (
            <button
                onClick={onToggle}
                style={{
                    padding: "8px",
                    background: "rgba(255, 255, 255, 0.05)",
                    color: "#e5e7eb",
                    borderRadius: 8,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s ease"
                }}
                title="Maximize"
            >
                <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
            </button>
        )}
        {fullScreen && onMaximize && (
            <button
                onClick={onToggle}
                style={{
                    padding: "8px",
                    background: "rgba(255, 107, 53, 0.2)",
                    color: "#ff6b35",
                    border: "1px solid #ff6b35",
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s ease"
                }}
            >

            </button>
        )}
    </>
));

const ProbabilitySubtitle = React.memo(({ salesperson, region, fullScreen }) => (
    <p
        style={{
            color: "#94a3b8",
            fontSize: fullScreen ? 14 : 13,
            margin: "0 0 16px 0",
            textAlign: fullScreen ? "center" : "left",
        }}
    >
        Salesperson: <strong style={{ color: "#e2e8f0" }}>{salesperson}</strong> · Region:{" "}
        <strong style={{ color: "#e2e8f0" }}>{region === "all" ? "All Regions" : region}</strong>
    </p>
));

function ProbabilityAnalysis({ filterContext, bundleData, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const [summary, setSummary] = useState(null);
    const [byOutcome, setByOutcome] = useState([]);
    const [byJobType, setByJobType] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(`${API_BASE}/api/metrics/probability-analysis`, {
                params: {
                    salesperson,
                    year,
                    region: region || "all",
                },
            });
            const data = res.data?.data || {};
            setSummary(data.summary || null);
            setByOutcome(Array.isArray(data.by_outcome) ? data.by_outcome : []);
            setByJobType(Array.isArray(data.by_job_type) ? data.by_job_type : []);
        } catch (err) {
            console.error("ProbabilityAnalysis error", err);
            setError(err.message || "Failed to load probability analysis");
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region]);

    useEffect(() => {
        if (!salesperson || !year) return;

        if (bundleData) {
            try {
                const s = bundleData.data?.summary || null;
                const o = bundleData.data?.by_outcome || [];
                const jt = bundleData.data?.by_job_type || [];
                setSummary(s);
                setByOutcome(Array.isArray(o) ? o : []);
                setByJobType(Array.isArray(jt) ? jt : []);
                setLoading(false);
                setError("");
            } catch (err) {
                console.error("Error processing probability bundle:", err);
                setError("Failed to process probability analysis data");
            }
            return;
        }

        fetchData();
    }, [salesperson, year, region, bundleData, fetchData]);

    const hasData = useMemo(() => (
        summary &&
        (summary.closed_won_count > 0 || summary.closed_lost_count > 0)
    ), [summary]);

    const outcomeChartData = useMemo(() => byOutcome.map((row) => ({
        outcome: row.outcome,
        count: row.count || 0,
        value_m_aed: (row.value_aed || 0) / 1_000_000,
    })), [byOutcome]);

    const jobTypeMetrics = useMemo(() => {
        const jobTypeChartData = [];
        let wonStatic = 0;
        let wonDigital = 0;
        let wonBoth = 0;
        let lostStatic = 0;
        let lostDigital = 0;
        let lostBoth = 0;

        if (Array.isArray(byJobType) && byJobType.length > 0) {
            const jtMap = {}; // { job_type: { won: count, lost: count } }
            byJobType.forEach((row) => {
                const jt = row.job_type || "Unknown";
                const outcome = row.outcome || "";
                if (!jtMap[jt]) {
                    jtMap[jt] = { won: 0, lost: 0 };
                }
                if (outcome === "Closed Won") {
                    jtMap[jt].won += row.count || 0;
                    if (jt.toLowerCase() === "static") {
                        wonStatic += row.count || 0;
                    } else if (jt.toLowerCase() === "digital") {
                        wonDigital += row.count || 0;
                    } else if (jt.toLowerCase() === "both") {
                        wonBoth += row.count || 0;
                    }
                } else if (outcome === "Closed Lost") {
                    jtMap[jt].lost += row.count || 0;
                    if (jt.toLowerCase() === "static") {
                        lostStatic += row.count || 0;
                    } else if (jt.toLowerCase() === "digital") {
                        lostDigital += row.count || 0;
                    } else if (jt.toLowerCase() === "both") {
                        lostBoth += row.count || 0;
                    }
                }
            });
            Object.keys(jtMap).forEach((jt) => {
                jobTypeChartData.push({
                    job_type: jt,
                    count: jtMap[jt].won,
                    lost_count: jtMap[jt].lost,
                });
            });
        }
        return {
            jobTypeChartData,
            wonStatic,
            wonDigital,
            wonBoth,
            lostStatic,
            lostDigital,
            lostBoth,
        };
    }, [byJobType]);

    const handleToggle = useCallback(() => {
        if (onMaximize) onMaximize();
    }, [onMaximize]);

    const actionButtons = useMemo(() => (
        <ProbabilityHeaderActions onMaximize={onMaximize} fullScreen={fullScreen} onToggle={handleToggle} />
    ), [onMaximize, fullScreen, handleToggle]);


    if (!salesperson || !year) return null;

    return (
        <DashboardCard
            title={`Probability Analysis - ${year}`}
            actions={actionButtons}
            fullScreen={fullScreen}
            gradient="primary"
        >
            <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
                <ProbabilitySubtitle salesperson={salesperson} region={region} fullScreen={fullScreen} />

                {loading && (
                    <SkeletonCard height="500px" />
                )}
                {error && (
                    <EmptyState icon="️" message={error} size="medium" />
                )}
                {!loading && !error && !hasData && (
                    <EmptyState
                        icon=""
                        message="No Closed Won / Closed Lost data found."
                        size="medium"
                    />
                )}

                {hasData && (
                    <>
                        {/* Summary tiles */}
                        {summary && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                                    gap: "12px",
                                    marginBottom: "20px",
                                }}
                            >
                                {/* Total Count */}
                                <div
                                    style={{
                                        backgroundColor: "rgba(17, 24, 39, 0.6)",
                                        borderRadius: 12,
                                        padding: "12px 14px",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#94a3b8",
                                            fontSize: 11,
                                            marginBottom: 4,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px"
                                        }}
                                    >
                                        Total Deals
                                    </div>
                                    <div
                                        style={{
                                            color: "#ffffff",
                                            fontSize: 20,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {summary.closed_won_count + summary.closed_lost_count}
                                    </div>
                                </div>

                                {/* Closed Won count */}
                                <div
                                    style={{
                                        backgroundColor: "rgba(16, 185, 129, 0.1)",
                                        borderRadius: 12,
                                        padding: "12px 14px",
                                        border: "1px solid rgba(16, 185, 129, 0.2)",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#34d399",
                                            fontSize: 11,
                                            marginBottom: 4,
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px"
                                        }}
                                    >
                                        Won
                                    </div>
                                    <div
                                        style={{
                                            color: "#ffffff",
                                            fontSize: 20,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {summary.closed_won_count}
                                    </div>
                                </div>

                                {/* Closed Lost count */}
                                <div
                                    style={{
                                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                                        borderRadius: 12,
                                        padding: "12px 14px",
                                        border: "1px solid rgba(239, 68, 68, 0.2)",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#f87171",
                                            fontSize: 11,
                                            marginBottom: 4,
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px"
                                        }}
                                    >
                                        Lost
                                    </div>
                                    <div
                                        style={{
                                            color: "#ffffff",
                                            fontSize: 20,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {summary.closed_lost_count}
                                    </div>
                                </div>

                                {/* Win rate */}
                                <div
                                    style={{
                                        backgroundColor: "rgba(255, 107, 53, 0.1)",
                                        borderRadius: 12,
                                        padding: "12px 14px",
                                        border: "1px solid rgba(255, 107, 53, 0.2)",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#fdba74",
                                            fontSize: 11,
                                            marginBottom: 4,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px"
                                        }}
                                    >
                                        Win Rate
                                    </div>
                                    <div
                                        style={{
                                            color: "#ff6b35",
                                            fontSize: 22,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {summary.win_rate_percent.toFixed(1)}%
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Second row: job-type breakdown tiles */}
                        {byJobType.length > 0 && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: "12px",
                                    marginBottom: "24px",
                                }}
                            >
                                {/* Total Won Value */}
                                <div style={{ backgroundColor: "rgba(16, 185, 129, 0.05)", borderRadius: 12, padding: "12px 16px", border: "1px solid rgba(16, 185, 129, 0.15)" }}>
                                    <div style={{ color: "#34d399", fontSize: 12, marginBottom: 4, fontWeight: 600 }}>Total Won Value ({displayCurrency})</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: "bold" }}>{displayCurrency} {Math.round(summary.total_closed_won_value_aed).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                    <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af", display: "flex", gap: "10px" }}>
                                        <span>Static: <strong style={{ color: "#fff" }}>{jobTypeMetrics.wonStatic}</strong></span>
                                        <span>Digital: <strong style={{ color: "#fff" }}>{jobTypeMetrics.wonDigital}</strong></span>
                                        <span>Both: <strong style={{ color: "#fff" }}>{jobTypeMetrics.wonBoth}</strong></span>
                                    </div>
                                </div>

                                {/* Total Lost Value */}
                                <div style={{ backgroundColor: "rgba(239, 68, 68, 0.05)", borderRadius: 12, padding: "12px 16px", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                                    <div style={{ color: "#f87171", fontSize: 12, marginBottom: 4, fontWeight: 600 }}>Total Lost Value ({displayCurrency})</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: "bold" }}>{displayCurrency} {Math.round(summary.total_closed_lost_value_aed).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                    <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af", display: "flex", gap: "10px" }}>
                                        <span>Static: <strong style={{ color: "#fff" }}>{jobTypeMetrics.lostStatic}</strong></span>
                                        <span>Digital: <strong style={{ color: "#fff" }}>{jobTypeMetrics.lostDigital}</strong></span>
                                        <span>Both: <strong style={{ color: "#fff" }}>{jobTypeMetrics.lostBoth}</strong></span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Charts */}
                        <h3 style={{ color: "#e5e7eb", margin: "0 0 12px", fontSize: 20, fontWeight: 600 }}>
                            ErpAnalyticsOpportunities Breakdown (By Value)
                        </h3>

                        <div style={{ height: 260, marginBottom: 24 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={outcomeChartData}
                                    margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                                    <XAxis
                                        dataKey="outcome"
                                        stroke="#94a3b8"
                                        tick={{ fill: "#94a3b8", fontSize: 12 }}
                                        axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="#94a3b8"
                                        tick={{ fill: "#94a3b8", fontSize: 12 }}
                                        tickFormatter={(v) => `${v.toFixed(1)}M`}
                                        axisLine={false}
                                        tickLine={false}
                                        width={40}
                                    />
                                <Tooltip
                                        contentStyle={{ backgroundColor: "#1e293b", borderColor: "rgba(255,255,255,0.1)", color: "#f1f5f9" }}
                                        formatter={(value) => [`${displayCurrency} ${Math.round(value * 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, "Value"]}
                                        cursor={{ fill: "rgba(255,255,255,0.05)" }}
                                    />
                                    <Bar dataKey="value_m_aed" name={`Value (Millions ${displayCurrency})`} fill="#ff6b35" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {byJobType.length > 0 && (
                            <>
                                <h3 style={{ color: "#e5e7eb", margin: "0 0 12px", fontSize: 20, fontWeight: 600 }}>
                                    Job Type Breakdown
                                </h3>
                                <div style={{ height: 240 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={jobTypeMetrics.jobTypeChartData}
                                            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                                            <XAxis
                                                dataKey="job_type"
                                                stroke="#94a3b8"
                                                tick={{ fill: "#94a3b8", fontSize: 12 }}
                                                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                stroke="#94a3b8"
                                                tick={{ fill: "#94a3b8", fontSize: 12 }}
                                                axisLine={false}
                                                tickLine={false}
                                                width={30}
                                            />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: "#1e293b", borderColor: "rgba(255,255,255,0.1)", color: "#f1f5f9" }}
                                                cursor={{ fill: "rgba(255,255,255,0.05)" }}
                                            />
                                            <Legend wrapperStyle={{ paddingTop: "10px" }} />
                                            <Bar dataKey="count" name="Won" fill="#10b981" radius={[4, 4, 0, 0]} stackId="a" />
                                            <Bar dataKey="lost_count" name="Lost" fill="#ef4444" radius={[4, 4, 0, 0]} stackId="a" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </DashboardCard>
    );
}

export default ProbabilityAnalysis;
